import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Save, Sparkles, X } from "lucide-react";
import { BUILTIN_PRESETS, NO_FX } from "@/lib/vocalPresets";
import { createCustomSound, deleteCustomSound, listCustomSounds } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";

const SLIDERS = {
  eq: [["low", "Low", -12, 12, 0.5, "dB"], ["mid", "Mid", -12, 12, 0.5, "dB"], ["high", "High", -12, 12, 0.5, "dB"]],
  compressor: [["threshold", "Threshold", -60, 0, 1, "dB"], ["ratio", "Ratio", 1, 20, 0.5, ":1"]],
  reverb: [["send", "Amount", 0, 1, 0.01, ""]],
  delay: [["time", "Time", 0.05, 1, 0.005, "s"], ["feedback", "Repeats", 0, 0.9, 0.01, ""]],
  limiter: [["threshold", "Ceiling", -24, 0, 0.5, "dB"]],
};
const NAMES = { eq: "Tone (EQ)", compressor: "Compressor", reverb: "Reverb", delay: "Delay", limiter: "Limiter" };

function Effect({ k, fx, onChange }) {
  const e = fx[k];
  return (
    <div data-testid={`rec-fx-${k}`} className={`rounded-xl border p-2.5 ${e.on ? "border-rose-300/30 bg-rose-500/[0.05]" : "border-white/[0.07] bg-black/20"}`}>
      <label className="flex cursor-pointer items-center justify-between text-[12px] font-semibold">
        {NAMES[k]}
        <input type="checkbox" data-testid={`rec-fx-${k}-toggle`} checked={e.on} onChange={(ev) => onChange(k, { on: ev.target.checked })} className="h-4 w-4 accent-rose-400" />
      </label>
      {e.on ? SLIDERS[k].map(([p, label, min, max, step, unit]) => (
        <label key={p} className="mt-1.5 block font-mono text-[9px] uppercase tracking-wider text-white/45">
          <span className="flex justify-between">{label}<span className="text-white/80">{Number(e[p]).toFixed(step < 0.1 ? 2 : 1)}{unit}</span></span>
          <input type="range" data-testid={`rec-fx-${k}-${p}`} min={min} max={max} step={step} value={e[p]} onChange={(ev) => onChange(k, { [p]: Number(ev.target.value) })} className="h-1.5 w-full accent-rose-300" />
        </label>
      )) : null}
    </div>
  );
}

/** Vocal sound for the selected track: 6 gospel presets + your own saved sounds, every effect adjustable or off. */
export default function VocalSoundPanel({ track, onChange }) {
  const [custom, setCustom] = useState([]);
  const [naming, setNaming] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { listCustomSounds().then(setCustom).catch(() => {}); }, []);
  if (!track) return null;
  const presets = [...BUILTIN_PRESETS, ...custom.map((c) => ({ key: `custom:${c.id}`, label: c.name, hint: "Your sound", fx: c.fx, id: c.id }))];
  const pick = (p) => onChange({ preset: p?.key || null, fx: JSON.parse(JSON.stringify(p?.fx || NO_FX)) });
  const tweak = (k, patch) => onChange({ fx: { ...track.fx, [k]: { ...track.fx[k], ...patch } } });
  const save = async () => {
    setBusy(true);
    try { const c = await createCustomSound({ name: naming.trim(), based_on: track.preset, fx: track.fx }); setCustom((x) => [...x, c]); onChange({ preset: `custom:${c.id}` }); setNaming(null); toast.success(`Saved “${c.name}”`); }
    catch (e) { toast.error("Couldn't save your sound", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    try { await deleteCustomSound(p.id); setCustom((x) => x.filter((c) => c.id !== p.id)); } catch (e) { toast.error("Couldn't delete it", { description: safeApiError(e) }); }
  };
  return (
    <section data-testid="rec-sound-panel" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-4">
      <p className="flex items-center gap-2 font-display text-[15px] font-semibold"><Sparkles className="h-4 w-4 text-rose-200" /> Vocal sound · {track.name}</p>
      <p className="mt-1 text-[11px] text-white/45">Real effects on playback and export — your recorded take is never changed.</p>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button type="button" data-testid="rec-preset-none" onClick={() => pick(null)} className={`rounded-xl border p-2 text-left text-[12px] ${!track.preset ? "border-rose-300/60 bg-rose-500/15" : "border-white/10 hover:bg-white/[0.06]"}`}>No effects</button>
        {presets.map((p) => (
          <div key={p.key} className="relative">
            <button type="button" data-testid={`rec-preset-${p.key.replace(":", "-")}`} aria-pressed={track.preset === p.key} onClick={() => pick(p)} title={p.hint}
              className={`w-full rounded-xl border p-2 pr-6 text-left text-[12px] transition-colors duration-150 ${track.preset === p.key ? "border-rose-300/60 bg-rose-500/15" : "border-white/10 hover:bg-white/[0.06]"}`}>
              <span className="block truncate font-semibold">{p.label}</span>
              <span className="block truncate text-[10px] text-white/45">{p.hint}</span>
            </button>
            {track.preset === p.key ? <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-rose-200" /> : null}
            {p.id ? <button type="button" data-testid={`rec-preset-delete-${p.id}`} onClick={() => remove(p)} aria-label="Delete sound" className="absolute bottom-1.5 right-1.5 text-white/30 hover:text-rose-300"><X className="h-3 w-3" /></button> : null}
          </div>
        ))}
      </div>
      <div className="mt-3 space-y-1.5">{Object.keys(SLIDERS).map((k) => <Effect key={k} k={k} fx={track.fx} onChange={tweak} />)}</div>
      {naming === null ? (
        <button type="button" data-testid="rec-sound-save-open" onClick={() => setNaming("")} className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-rose-200 hover:text-white"><Save className="h-3 w-3" /> Save as my own sound</button>
      ) : (
        <div className="mt-3 flex gap-1.5">
          <input data-testid="rec-sound-name" value={naming} onChange={(e) => setNaming(e.target.value)} maxLength={60} placeholder="e.g. Sunday Morning Voice" className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-[12px] focus:outline-none" />
          <button type="button" data-testid="rec-sound-save" onClick={save} disabled={busy || !naming.trim()} className="rounded-lg bg-rose-500 px-3 text-[12px] font-semibold disabled:opacity-40">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</button>
        </div>
      )}
    </section>
  );
}
